/**
 * Camada 0 · idioma, PT e EN.
 *
 * DUAS DECISOES QUE SUSTENTAM ESTE PACOTE, e sao o motivo dele existir assim:
 *
 * 1. A CHAVE DO DICIONARIO E A FRASE EM PORTUGUES, nunca um codigo. Duas razoes, e a
 *    segunda e a que pesa: o texto continua legivel dentro do componente, e a frase sem
 *    traducao DEGRADA PARA O PORTUGUES, que alguem consegue ler, em vez de virar
 *    "menu.item.titulo" na tela do cliente.
 *
 * 2. A PREFERENCIA VAI EM COOKIE, e nao so em localStorage. Onde a tela renderiza no
 *    servidor (o site institucional em Astro), o servidor precisa saber o idioma ANTES
 *    de montar o HTML. Com localStorage, a pagina chegaria em portugues e piscaria para
 *    o ingles em toda navegacao. O localStorage entra como espelho, para leitura rapida
 *    dentro do navegador.
 *
 * O QUE NAO SE TRADUZ fica declarado em NAO_TRADUZIR, mais abaixo. Traduzir qualquer um
 * daqueles itens produz um texto que nao existe em lugar nenhum e o apresenta como se
 * fosse o original.
 */

export const IDIOMAS = ['pt', 'en'] as const;
export type Idioma = (typeof IDIOMAS)[number];

/** O idioma da casa. Frase sem traducao cai aqui, e por isso a queda e legivel. */
export const IDIOMA_PADRAO: Idioma = 'pt';

/**
 * Os idiomas LIGADOS neste projeto, e so o portugues.
 *
 * Decisao do fundador em 19/09/2026: "nada aqui tera PT EN". E excecao declarada a regra global de
 * que todo sistema nasce bilingue, e mora no CLAUDE.md do projeto.
 *
 * POR QUE `IDIOMAS` CONTINUA COM O INGLES: os dicionarios EN dos pacotes do Chassi usam o tipo
 * `Idioma`, e tira-lo quebraria a compilacao de todos. O que decide o que a pessoa ve e ESTA lista:
 * o controle de idioma so aparece com mais de um ligado, e um idioma guardado fora dela cai no
 * padrao em `idiomaAtual()`.
 *
 * Divergencia protegida do Chassi da plataforma (`scripts/sincronizar-chassi.sh`).
 */
export const IDIOMAS_ATIVOS: readonly Idioma[] = Object.freeze(['pt'] as Idioma[]);

/** Nome do cookie e da chave de armazenamento. Um so cada, exportados, para nenhum app
 *  inventar o proprio e a preferencia se perder ao trocar de produto no mesmo dominio. */
export const COOKIE_IDIOMA = 'cx_idioma';
export const CHAVE_IDIOMA = 'cx.idioma';

/** O valor que vai no atributo lang do documento. Leitor de tela escolhe a voz por ele,
 *  e deixa-lo em pt-BR com a tela em ingles faz a leitura sair com a pronuncia errada. */
export const LANG_HTML: Record<Idioma, string> = {
  pt: 'pt-BR',
  en: 'en-US',
};

/**
 * O que NAO se traduz, nunca, em nenhum produto.
 *
 * Esta lista e do dominio e cresce com ele. Cada item esta aqui porque traduzi-lo
 * produziria um documento que nao existe: uma lei com nome inventado, uma citacao que
 * ninguem disse, um nome proprio que nao e de ninguem.
 */
export const NAO_TRADUZIR = [
  'documento de fonte oficial, com o texto exato como foi emitido',
  'nome de lei, de artigo e de norma',
  'citacao literal de terceiro',
  'nome proprio de pessoa, de empresa e de orgao',
  'conteudo escrito pelo proprio usuario',
  'identificador tecnico visivel (numero de protocolo, codigo de nota, hash)',
] as const;

/** Dicionario de um idioma: frase em portugues para frase traduzida. */
export type Dicionario = Record<string, string>;

/** Dicionarios registrados por idioma. O de portugues fica vazio de proposito: a chave
 *  JA E o portugues, entao repeti-lo seria manter a mesma frase em dois lugares, e um
 *  dos dois envelheceria. */
const dicionarios: Record<Idioma, Dicionario> = {
  pt: {},
  en: {},
};

/**
 * Registra ou amplia o dicionario de um idioma.
 *
 * Cada pacote e cada produto chama isto com as proprias frases, em vez de existir um
 * arquivo gigante central: assim tela nova nasce traduzida no mesmo commit em que nasce,
 * que e o unico jeito de a traducao alcancar a TELA INTEIRA e nao so o cabecalho.
 */
export function registrarDicionario(idioma: Idioma, entradas: Dicionario): void {
  dicionarios[idioma] = { ...dicionarios[idioma], ...entradas };
}

/** Le a preferencia do cookie. Funciona no navegador; no servidor, quem le e o
 *  framework, que passa o valor por `definirIdiomaEmMemoria`. */
function idiomaDoCookie(): Idioma | null {
  if (typeof document === 'undefined') return null;
  const achado = document.cookie
    .split(';')
    .map((p) => p.trim())
    .find((p) => p.startsWith(`${COOKIE_IDIOMA}=`));
  if (!achado) return null;
  const valor = achado.slice(COOKIE_IDIOMA.length + 1);
  return IDIOMAS.includes(valor as Idioma) ? (valor as Idioma) : null;
}

/** Espelho em localStorage, para leitura sincrona rapida dentro do navegador. */
function idiomaDoArmazenamento(): Idioma | null {
  if (typeof window === 'undefined') return null;
  try {
    const valor = window.localStorage.getItem(CHAVE_IDIOMA);
    return IDIOMAS.includes(valor as Idioma) ? (valor as Idioma) : null;
  } catch {
    // Janela privada e armazenamento bloqueado lancam ao ler. Preferencia visual nao
    // pode derrubar a aplicacao.
    return null;
  }
}

/** Idioma corrente em memoria. Existe para a renderizacao no servidor conseguir fixar
 *  o idioma do pedido antes de montar o HTML, sem depender de cookie do navegador. */
let idiomaEmMemoria: Idioma | null = null;

/** Fixa o idioma do pedido atual. Chamado pelo servidor, uma vez por requisicao. */
export function definirIdiomaEmMemoria(idioma: Idioma): void {
  idiomaEmMemoria = idioma;
}

/** O idioma que vale agora: o do pedido, depois o cookie, depois o espelho, depois o padrao. */
export function idiomaAtual(): Idioma {
  const escolhido = idiomaEmMemoria ?? idiomaDoCookie() ?? idiomaDoArmazenamento() ?? IDIOMA_PADRAO;
  // Idioma guardado que nao esta ligado neste projeto cai no padrao. O caso real: o cookie
  // `cx_idioma` do localhost e compartilhado entre portas, e um "en" escolhido num app da
  // plataforma apareceria aqui.
  return IDIOMAS_ATIVOS.includes(escolhido) ? escolhido : IDIOMA_PADRAO;
}

/**
 * Guarda a escolha nos dois lugares e atualiza o atributo lang do documento.
 *
 * O cookie leva SameSite=Lax e um ano de validade: Lax porque a preferencia precisa
 * sobreviver a navegacao entre os produtos do mesmo dominio, e um ano porque ninguem
 * quer reescolher o idioma toda semana.
 */
export function definirIdioma(idioma: Idioma): void {
  idiomaEmMemoria = idioma;

  if (typeof document !== 'undefined') {
    const umAno = 60 * 60 * 24 * 365;
    document.cookie = `${COOKIE_IDIOMA}=${idioma}; path=/; max-age=${umAno}; SameSite=Lax`;
    document.documentElement.lang = LANG_HTML[idioma];
  }

  try {
    window.localStorage.setItem(CHAVE_IDIOMA, idioma);
  } catch {
    // Ver comentario em idiomaDoArmazenamento.
  }
}

/**
 * O nome do evento que anuncia a troca de idioma, exportado para ninguem escreve-lo a mao.
 *
 * ELE EXISTE PORQUE `t()` NAO E REATIVA: ela le o idioma no instante em que roda, e o React
 * nao tem como saber que a resposta mudou. Sem um aviso, trocar o idioma atualiza o cookie
 * e o `lang` e deixa a tela inteira desenhada em portugues ate a proxima navegacao, que e
 * "metade da interface trocando" com outra roupa, e isso e pior que seletor nenhum.
 *
 * O nome mora AQUI, e nao dentro de quem dispara: escrito a mao em dois lugares, um dos
 * dois envelhece no dia em que alguem corrigir a grafia, e o ouvinte para de ouvir EM
 * SILENCIO, sem erro nenhum na tela.
 */
export const EVENTO_IDIOMA = 'cx:idioma';

/**
 * Escreve no documento o idioma que JA esta valendo, lido do cookie e do espelho.
 *
 * O DEFEITO QUE ELA FECHA, medido em 06/09/2026 no VOE: `definirIdioma` e o UNICO lugar que
 * escreve o atributo `lang`, e ela so roda quando alguem CLICA. Na carga da pagina ninguem
 * a chama, entao o `<html lang="pt-BR">` do `index.html` sobrevive a recarga mesmo com a
 * preferencia gravada em `en`. O app SABE que esta em ingles e continua dizendo ao leitor
 * de tela que a pagina e portuguesa, e o leitor escolhe a VOZ por esse atributo: o texto
 * ingles sai com pronuncia portuguesa.
 *
 * Idempotente e sem efeito fora do navegador, para poder ser chamada na carga do modulo do
 * app sem quebrar onde a tela e montada no servidor.
 */
export function sincronizarLangDoDocumento(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = LANG_HTML[idiomaAtual()];
}

/**
 * Escuta a troca de idioma e devolve a funcao que PARA de escutar.
 *
 * Devolver o cancelador nao e formalidade: sem chamar o retorno ao desmontar, cada montagem
 * soma um ouvinte que nunca sai, e a mesma troca passa a redesenhar N vezes, uma por
 * montagem que ja aconteceu na vida daquela aba.
 *
 * ELA NAO IMPORTA REACT DE PROPOSITO. Este pacote e camada 0 e nao importa nada, e e isso
 * que permite ao servidor e a um script de medicao usarem `t()` sem arrastar a arvore de
 * componentes junto. Quem precisa de redesenho liga isto a um estado no proprio app.
 */
export function assinarIdioma(ouvinte: (idioma: Idioma) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const aoTrocar = () => ouvinte(idiomaAtual());
  window.addEventListener(EVENTO_IDIOMA, aoTrocar);
  return () => window.removeEventListener(EVENTO_IDIOMA, aoTrocar);
}

/**
 * Traduz. A funcao curta de proposito, porque ela aparece em toda linha de toda tela.
 *
 * Sem entrada no dicionario, devolve a propria chave, que E a frase em portugues. E a
 * degradacao legivel que justifica o formato da chave.
 */
export function t(frase: string, idioma: Idioma = idiomaAtual()): string {
  if (idioma === IDIOMA_PADRAO) return frase;
  return dicionarios[idioma]?.[frase] ?? frase;
}

/**
 * Traduz com substituicao de valor: t2('Ola, {nome}', { nome: 'Ana' }).
 *
 * A substituicao acontece DEPOIS da traducao para o marcador poder mudar de posicao na
 * frase traduzida, que e o caso mais comum entre portugues e ingles.
 */
export function t2(
  frase: string,
  valores: Record<string, string | number>,
  idioma: Idioma = idiomaAtual(),
): string {
  const traduzida = t(frase, idioma);
  return traduzida.replace(/\{(\w+)\}/g, (inteiro, chave: string) =>
    chave in valores ? String(valores[chave]) : inteiro,
  );
}

/** Formata numero, data e moeda no locale do idioma. Fica aqui, e nao em cada tela,
 *  porque separador decimal e formato de data sao parte da traducao e sao esquecidos
 *  com a mesma frequencia com que o texto e lembrado. */
/**
 * O TRACO QUANDO O VALOR NAO E NUMERO, pela mesma razao registrada em `formatarNanoBrl`.
 *
 * O tipo diz `number` e o dado atravessa a rede: um campo ausente no JSON chega `undefined`,
 * e `Intl.NumberFormat.format(undefined)` devolve a string "NaN", com aparencia de numero.
 * Numero em tela ou e real ou e um traco, e zero seria pior que os dois, porque zero e um
 * valor calculado.
 */
const TRACO_NUMERO = '\u2014';

export function formatarNumero(valor: number, idioma: Idioma = idiomaAtual()): string {
  if (typeof valor !== 'number' || !Number.isFinite(valor)) return TRACO_NUMERO;
  return new Intl.NumberFormat(LANG_HTML[idioma]).format(valor);
}

export function formatarMoeda(
  valor: number,
  moeda: string,
  idioma: Idioma = idiomaAtual(),
): string {
  // Mesma guarda de `formatarNumero`: dinheiro com NaN e pior que numero com NaN, porque
  // vem com o simbolo da moeda na frente e passa por valor conferido.
  if (typeof valor !== 'number' || !Number.isFinite(valor)) return TRACO_NUMERO;
  return new Intl.NumberFormat(LANG_HTML[idioma], { style: 'currency', currency: moeda }).format(
    valor,
  );
}

export function formatarData(
  valor: Date,
  idioma: Idioma = idiomaAtual(),
  opcoes: Intl.DateTimeFormatOptions = { dateStyle: 'short' },
): string {
  return new Intl.DateTimeFormat(LANG_HTML[idioma], opcoes).format(valor);
}
